import { Course } from '@mc/api'
import { CourseSubType } from 'libs/constants'

export const formatTitle = (
  isMultiInstructor: boolean | null | undefined,
  skill: string | null | undefined,
  instructorName: string | null | undefined,
) => (isMultiInstructor ? skill : instructorName)

export const formatSubtitle = (
  title: string | null | undefined,
  instructorName: string | null | undefined,
  isMultiInstructor: boolean | null | undefined,
  multiInstructorName: string | null | undefined,
) => {
  if (isMultiInstructor) return multiInstructorName

  // If the title doesn't contain instructor name,
  // just return it
  if (
    instructorName &&
    title?.toLowerCase().indexOf(instructorName.toLowerCase()) === -1
  ) {
    return title
  }

  // If it has an instructor name and the word
  // "teach" or "teaches" in it, strip out
  // the instrutor name and return what they teach
  if (title?.toLowerCase().match(/teach/)) {
    const startFrom = title.toLowerCase().match(/teach/)?.index
    if (startFrom) {
      const teaches = title.substr(startFrom)

      return teaches
    }
  }

  // Otherwise they don't have "teach" in their
  // class title, so just return it from where
  // their name ends
  // only if the instructor name is at the beginning
  if (instructorName && title?.startsWith(instructorName)) {
    const rmInstructor = title.trim().slice(instructorName.length)
    // edge case where there is a colon after the instructor name
    // James Clear course is an example
    if (rmInstructor.startsWith(':')) {
      return rmInstructor.slice(1).trim()
    }
    return rmInstructor
  }

  return title
}

export const pruneRelatedCourses = (courses: Course[]) =>
  courses.map(
    ({
      brightcoveVideoId,
      mediaUuid,
      headline,
      id,
      instructorName,
      isMultiInstructor,
      multiInstructorName,
      nameplate,
      numChapters,
      primary1x1,
      primary2x3,
      primary16x9,
      skill,
      slug,
      title,
      tag,
      type,
      totalSeconds,
      primaryCategory,
    }) => ({
      brightcoveVideoId,
      mediaUuid,
      headline,
      id,
      instructorName,
      isMultiInstructor,
      multiInstructorName,
      nameplate,
      numChapters,
      primary1x1,
      primary2x3,
      primary16x9,
      skill,
      slug,
      title,
      tag,
      type,
      totalSeconds,
      primaryCategory,
      displayTitle: formatTitle(isMultiInstructor, skill, instructorName),
      displaySubtitle: formatSubtitle(
        title,
        instructorName,
        isMultiInstructor,
        multiInstructorName,
      ),
    }),
  )

export type PrunedCourse = ReturnType<typeof pruneRelatedCourses>[number]

export const getIsSeries = (type: string | undefined) =>
  type === CourseSubType.SERIES

export const formatCourseDuration = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  const hoursStr = hours > 0 ? `${hours}h` : ''
  const minutesStr = minutes > 0 ? `${minutes}m` : ''

  return `${hoursStr} ${minutesStr}`.trim()
}
